import { useCallback, useEffect, useState } from 'react';

type GetFn = <T>(path: string) => Promise<T>;

export const PWA_ALERTS_BADGE_REFRESH_EVENT = 'va-pwa-alerts-badge-refresh';

function applyAppBadge(count: number): void {
  const nav = navigator as Navigator & {
    setAppBadge?: (count: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  if (count > 0 && typeof nav.setAppBadge === 'function') {
    void nav.setAppBadge(count).catch(() => {});
  } else if (typeof nav.clearAppBadge === 'function') {
    void nav.clearAppBadge().catch(() => {});
  }
}

/** Pendientes en la lista de alertas (notificaciones operador abiertas no descartadas).
 *  No mezcla con “chats en modo humano”: al resolver una fila el conteo debe bajar aunque el chat siga en humano. */
export function useAlertsBadgeCount(
  get: GetFn,
  can: (required: string | string[]) => boolean,
  permissionsLoaded: boolean,
  pollMs = 20_000
): number {
  const [count, setCount] = useState(0);
  const canNotif = can('pwa.notifications.read');
  const enabled = permissionsLoaded && canNotif;

  const refresh = useCallback(async () => {
    if (!enabled) {
      setCount(0);
      applyAppBadge(0);
      return;
    }
    try {
      const d = await get<{ count: number }>('/api/console/notifications/unread-count');
      const n = Math.max(0, Number(d.count) || 0);
      setCount(n);
      applyAppBadge(n);
    } catch {
      /* red: mantener último valor */
    }
  }, [get, enabled]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const id = window.setInterval(() => void refresh(), pollMs);
    const onVis = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const onBadgeEvent = () => void refresh();
    const onFocus = () => void refresh();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(PWA_ALERTS_BADGE_REFRESH_EVENT, onBadgeEvent);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(PWA_ALERTS_BADGE_REFRESH_EVENT, onBadgeEvent);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onFocus);
    };
  }, [refresh, enabled, pollMs]);

  return count;
}
